import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Play, Pause, RotateCcw, RotateCw,
  Volume2, VolumeX, Maximize, Minimize,
} from 'lucide-react';
import Section from '../layout/Section';
import SectionTitle from '../ui/SectionTitle';
import ScrollReveal from '../animations/ScrollReveal';
import MouseParallax from '../animations/MouseParallax';
import StaggerContainer, { staggerItem } from '../animations/StaggerContainer';

const demoVideo = '/demo/DEMO.mp4';

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

const stats = [
  { icon: '⚡', value: 'Ultra-rapide', label: 'Encaissement en 30 sec' },
  { icon: '📊', value: '100%', label: 'Données en temps réel' },
  { icon: '📱', value: '3 appareils', label: 'Synchronisés' },
];

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

export default function DemoVideoSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);

  /* ── Auto-hide controls ── */
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying) {
      setShowControls(true);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    } else {
      resetHideTimer();
    }
    return () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
  }, [isPlaying, resetHideTimer]);

  /* ── Fullscreen change listener ── */
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  /* ── Controls ── */
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
      setIsStarted(true);
    }
  };

  const skip = (sec: number) => {
    if (videoRef.current) videoRef.current.currentTime += sec;
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = Number(e.target.value);
    if (videoRef.current) videoRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const handleVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = v;
      videoRef.current.muted = v === 0;
    }
    setVolume(v);
    setIsMuted(v === 0);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const next = !isMuted;
    videoRef.current.muted = next;
    setIsMuted(next);
    if (!next && volume === 0) {
      videoRef.current.volume = 0.5;
      setVolume(0.5);
    }
  };

  const changeSpeed = (rate: number) => {
    if (videoRef.current) videoRef.current.playbackRate = rate;
    setPlaybackRate(rate);
    setShowSpeedMenu(false);
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!document.fullscreenElement) el?.requestFullscreen();
    else document.exitFullscreen();
  };

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const volumePct = isMuted ? 0 : volume * 100;

  return (
    <Section dark id="demo">
      {/* Central glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[700px] h-[400px] rounded-full bg-primary/10 blur-[120px] animate-glow-pulse" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto">
        {/* Badge + Title */}
        <div className="text-center mb-3">
          <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-primary/10 text-primary border border-primary/20">
            Démo produit
          </span>
        </div>

        <SectionTitle
          title="Voyez XamXamBoutik en action"
          subtitle="En moins de 2 minutes, découvrez comment transformer votre gestion de boutique."
          dark
        />

        {/* Video player */}
        <ScrollReveal direction="up" delay={0.1} className="mt-6 sm:mt-10">
          <MouseParallax strength={4}>
            <div
              ref={containerRef}
              className="relative rounded-2xl overflow-hidden card-glow border border-white/10 bg-[#0A0A0F] group"
              onMouseMove={resetHideTimer}
              onClick={() => { if (isStarted) togglePlay(); }}
            >
              {/* Video */}
              <div className="aspect-video">
                <video
                  ref={videoRef}
                  src={demoVideo}
                  loop
                  playsInline
                  preload="auto"
                  className="w-full h-full object-contain"
                  onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime ?? 0)}
                  onLoadedMetadata={() => setDuration(videoRef.current?.duration ?? 0)}
                  onEnded={() => setIsPlaying(false)}
                />
              </div>

              {/* Big play button overlay (initial state) */}
              <AnimatePresence>
                {!isStarted && (
                  <motion.div
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,0.35)' }}
                    onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                  >
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/40 cursor-pointer"
                    >
                      <Play className="w-7 h-7 sm:w-9 sm:h-9 text-white ml-1" />
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Controls bar */}
              <AnimatePresence>
                {isStarted && showControls && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute bottom-0 left-0 right-0 px-3 pb-3 pt-8"
                    style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Progress bar */}
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="range"
                        className="video-range flex-1"
                        min={0}
                        max={duration || 100}
                        step={0.1}
                        value={currentTime}
                        onChange={handleSeek}
                        style={{ '--pct': `${progressPct}%` } as React.CSSProperties}
                      />
                      <span className="text-white/70 text-xs tabular-nums whitespace-nowrap">
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </span>
                    </div>

                    {/* Buttons row */}
                    <div className="flex items-center gap-1 sm:gap-2">
                      {/* Rewind */}
                      <button
                        onClick={() => skip(-10)}
                        className="p-1.5 text-white/80 hover:text-white transition-colors"
                        title="-10s"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>

                      {/* Play/Pause */}
                      <button
                        onClick={togglePlay}
                        className="p-1.5 text-white hover:text-primary transition-colors"
                      >
                        {isPlaying
                          ? <Pause className="w-5 h-5" />
                          : <Play className="w-5 h-5 ml-0.5" />
                        }
                      </button>

                      {/* Forward */}
                      <button
                        onClick={() => skip(10)}
                        className="p-1.5 text-white/80 hover:text-white transition-colors"
                        title="+10s"
                      >
                        <RotateCw className="w-4 h-4" />
                      </button>

                      {/* Volume */}
                      <div className="flex items-center gap-1.5 ml-1">
                        <button
                          onClick={toggleMute}
                          className="p-1 text-white/80 hover:text-white transition-colors"
                        >
                          {isMuted || volume === 0
                            ? <VolumeX className="w-4 h-4" />
                            : <Volume2 className="w-4 h-4" />
                          }
                        </button>
                        <input
                          type="range"
                          className="video-range w-16 sm:w-20"
                          min={0}
                          max={1}
                          step={0.05}
                          value={isMuted ? 0 : volume}
                          onChange={handleVolume}
                          style={{ '--pct': `${volumePct}%` } as React.CSSProperties}
                        />
                      </div>

                      {/* Spacer */}
                      <div className="flex-1" />

                      {/* Speed */}
                      <div className="relative">
                        <button
                          onClick={() => setShowSpeedMenu((v) => !v)}
                          className="px-2 py-1 text-white/80 hover:text-white text-xs font-semibold transition-colors rounded border border-white/20 hover:border-white/40"
                        >
                          {playbackRate === 1 ? '1×' : `${playbackRate}×`}
                        </button>
                        <AnimatePresence>
                          {showSpeedMenu && (
                            <motion.div
                              initial={{ opacity: 0, y: 4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 4 }}
                              transition={{ duration: 0.15 }}
                              className="absolute bottom-full right-0 mb-2 bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden shadow-xl"
                            >
                              {SPEEDS.map((s) => (
                                <button
                                  key={s}
                                  onClick={() => changeSpeed(s)}
                                  className={`block w-full px-4 py-2 text-xs text-left transition-colors hover:bg-white/10 ${
                                    playbackRate === s
                                      ? 'text-primary font-bold'
                                      : 'text-white/80'
                                  }`}
                                >
                                  {s === 1 ? '1× (Normal)' : `${s}×`}
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Fullscreen */}
                      <button
                        onClick={toggleFullscreen}
                        className="p-1.5 text-white/80 hover:text-white transition-colors"
                      >
                        {isFullscreen
                          ? <Minimize className="w-4 h-4" />
                          : <Maximize className="w-4 h-4" />
                        }
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </MouseParallax>
        </ScrollReveal>

        {/* Stats */}
        <StaggerContainer
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 sm:mt-8"
          delay={0.2}
        >
          {stats.map((stat) => (
            <motion.div
              key={stat.value}
              variants={staggerItem}
              className="glass rounded-2xl p-5 text-center card-shimmer"
            >
              <div className="text-2xl mb-2">{stat.icon}</div>
              <div className="text-white font-black text-2xl">{stat.value}</div>
              <p className="text-gray-400 text-sm mt-1">{stat.label}</p>
            </motion.div>
          ))}
        </StaggerContainer>
      </div>
    </Section>
  );
}
